import { motion } from 'motion/react';
import { UserPlus, User } from 'lucide-react';
import { Button } from './ui/button';
import RankBadge from './RankBadge';

interface MatchPopupProps {
  user: any;
  userProfile: any;
  onClose: () => void;
  onViewProfile: () => void;
}

export function MatchPopup({ user, userProfile, onClose, onViewProfile }: MatchPopupProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/30 dark:bg-black/50 backdrop-blur-md z-50 flex items-center justify-center p-6"
      onClick={onClose}
    >

      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 20 }}
        className="bg-[#FDFBF7] dark:bg-[#1a1a1a] rounded-3xl p-8 max-w-md w-full border border-black/10 dark:border-white/10 shadow-2xl relative overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Celebration Animation */}
        <div className="text-center mb-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.2, 1] }}
            transition={{ duration: 0.6 }}
            className="w-24 h-24 mx-auto mb-4 rounded-full bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] flex items-center justify-center"
          >
            <UserPlus className="w-12 h-12 text-white" />
          </motion.div>

          <motion.h2
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-3xl text-black dark:text-white mb-2 leading-tight"
          >
            You are now friends with {user.name}!
          </motion.h2>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-[#666666] dark:text-[#E5E5E5]"
          >
            You and {user.name} are now connected. Start a conversation, slap them or stalk their yaps.
          </motion.p>
        </div>

        {/* User Preview */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mb-6"
        >
          <div className="relative w-full h-48 rounded-2xl overflow-hidden mb-4">
            <img
              src={user.image}
              alt={user.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-linear-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-4 left-4">
              <h3 className="flex items-center text-2xl text-white">
                <span>{user.name}</span>
                <RankBadge rank={user.rank} size={26} />
                <span>{user.age ? `, ${user.age}` : ''}</span>
              </h3>
            </div>
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="space-y-3"
        >
          <Button
            onClick={onViewProfile}
            className="w-full h-14 bg-linear-to-r from-[#F39C12] to-[#E74C3C] dark:from-[#4478ea] dark:to-[#2d1ef1] hover:opacity-90 transition-opacity text-white rounded-xl"
          >
            <User className="mr-2 w-5 h-5" />
            View Profile
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            className="w-full h-14 bg-transparent border-2 border-[#F39C12] dark:border-[#4478ea] text-[#F39C12] dark:text-[#4478ea] hover:bg-[#F39C12]/10 dark:hover:bg-[#4478ea]/10 transition-colors rounded-xl"
          >
            Keep Discovering
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}
